import {
  all,
  call,
  put,
  select,
  takeEvery,
} from 'redux-saga/effects';
import { uniqueByUUID } from '@/utils/uuidHelper';
import callAdapter from '@/utils/sagaHelper';
import selectors from '@/selectors';
import {
  showClauseCounts,
  showClauseCountsCancelled,
  showClauseCountsError,
  showClausesCounts,
  showClausesCountsCancelled,
  showClausesCountsError,
} from '../actions';
import actions from '../constants';
import CohortProfileService from '../service';
import validateCohortClauseCounts, { validateCohortClausesCounts } from '../helper';
import { findQuery, findQueryClause } from '../../state/shared';
import { getCohortSourceProps } from '../../sagaHelper';
import SystemCohortProfileService from '../../system/services/systemCohortProfileService';
import cohortClauseActions from '../../clauses/constants';
import cohortFilterActions from '../../filters/constants';

function* selectionClausesCounts(action) {
  const items = yield select(selectors.cohorts);
  const clauses = validateCohortClausesCounts(items, action, action.intl);

  if (clauses.length === 0) {
    yield put(showClausesCountsCancelled(action.category, action.code, action.queryUUID, action.dictionaryCode));
  } else {
    const { response, error } = yield call(callAdapter, CohortProfileService.selectionCounts(clauses));

    if (error) {
      yield put(showClausesCountsError(action.category, action.code, error, action.queryUUID, action.dictionaryCode));
    } else {
      yield put(showClausesCounts(
        action.category,
        action.code,
        response.total,
        response.total_filtered,
        response.entities,
        action.queryUUID,
        action.dictionaryCode,
      ));
    }
  }
}

function* cohortClausesCounts(action, isSystem) {
  const cohorts = yield select(selectors.cohorts);
  const clauses = validateCohortClausesCounts(cohorts, action, action.intl);

  if (clauses.length === 0) {
    yield put(showClausesCountsCancelled(action.category, action.code, action.queryUUID, action.dictionaryCode));
  } else {
    const { cohort, query } = findQuery(cohorts, action);
    let service = CohortProfileService;
    let params = { cohortCode: cohort.code, queryId: query.id, dictionaryCode: action.dictionaryCode };

    if (isSystem) {
      service = SystemCohortProfileService;
      params = { code: cohort.requestCode, queryId: query.id, dictionaryCode: action.dictionaryCode };
    }

    const { response, error } = yield call(callAdapter, service.queryCounts(params));
    if (error) {
      yield put(showClausesCountsError(action.category, action.code, error, action.queryUUID, action.dictionaryCode));
    } else {
      yield put(showClausesCounts(
        action.category,
        action.code,
        response.total,
        response.total_filtered,
        response.entities,
        action.queryUUID,
        action.dictionaryCode,
      ));
    }
  }
}

export function* clausesCounts(action) {
  const { isSystem, isEditing } = yield getCohortSourceProps(action);

  if (isEditing) {
    yield selectionClausesCounts(action);
  } else {
    yield cohortClausesCounts(action, isSystem);
  }
}

function* selectionClauseCounts(action) {
  const items = yield select(selectors.cohorts);
  const { dependents, parents, exists } = validateCohortClauseCounts(items, action);

  if (!exists && action.cascade) {
    yield call(clausesCounts, action);
  } else if (dependents.length === 0) {
    yield put(showClauseCountsCancelled(action.category, action.code, action.queryUUID, action.clauseUUID));
  } else {
    const { response, error } = yield call(callAdapter, CohortProfileService.selectionCounts(dependents));

    if (error) {
      yield put(showClauseCountsError(action.category, action.code, error, action.queryUUID, action.clauseUUID));
    } else {
      yield put(showClauseCounts(
        action.category,
        action.code,
        response.total,
        response.total_filtered,
        response.entities,
        action.queryUUID,
        action.clauseUUID,
      ));

      if (action.cascade) {
        const dictionaries = uniqueByUUID(dependents.map((c) => c.dictionary).concat(parents.map((c) => c.dictionary)));
        yield all(dictionaries.map((d) => clausesCounts({ ...action, dictionaryCode: d.code })));
        yield all(parents.map((c) => selectionClauseCounts({ ...action, clauseUUID: c.uuid })));
      }
    }
  }
}

function* cohortClauseCounts(action, isSystem) {
  const cohorts = yield select(selectors.cohorts);
  const { dependents } = validateCohortClauseCounts(cohorts, action);

  if (dependents.length === 0) {
    yield put(showClauseCountsCancelled(action.category, action.code, action.queryUUID, action.clauseUUID));
  } else {
    const { clause, cohort } = findQueryClause(cohorts, action);
    let service = CohortProfileService;
    let params = { cohortCode: cohort.code, clauseId: clause.id };

    if (isSystem) {
      service = SystemCohortProfileService;
      params = { code: cohort.requestCode, clauseId: clause.id };
    }

    const { response, error } = yield call(callAdapter, service.clauseCounts(params));
    if (error) {
      yield put(showClauseCountsError(action.category, action.code, error, action.queryUUID, action.clauseUUID));
    } else {
      yield put(showClauseCounts(
        action.category,
        action.code,
        response.total,
        response.total_filtered,
        response.entities,
        action.queryUUID,
        action.clauseUUID,
      ));
    }
  }
}

export function* clauseCounts(action) {
  const { isSystem, isEditing } = yield getCohortSourceProps(action);

  if (isEditing === true) {
    yield selectionClauseCounts(action);
  } else {
    yield cohortClauseCounts(action, isSystem);
  }
}

export default function* cohortProfileApi() {
  yield takeEvery(
    [actions.FETCH_CLAUSE_COUNTS, cohortFilterActions.ADD_QUERY_FILTER, cohortFilterActions.REMOVE_FILTER_FROM_COHORT],
    clauseCounts,
  );
  yield takeEvery(
    [actions.FETCH_CLAUSES_COUNTS, cohortClauseActions.REMOVE_COHORT_CLAUSE, cohortClauseActions.CLONE_COHORT_CLAUSE],
    clausesCounts,
  );
}
